import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import Pagination from '../../components/admin/Pagination';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { ShoppingCart, TrendingUp, Package, MailWarning, CheckCircle, Clock, RefreshCw, Star } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PERIODS = [
  { value: 'today', label: 'Dziś' },
  { value: 'yesterday', label: 'Wczoraj' },
  { value: '7d', label: '7 dni' },
  { value: '30d', label: '30 dni' },
  { value: '90d', label: '90 dni' },
  { value: '1y', label: 'Rok' },
  { value: 'all', label: 'Wszystko' },
];

const TABS = [
  { id: 'sales', label: 'Sprzedaż', icon: TrendingUp },
  { id: 'carts', label: 'Porzucone koszyki', icon: ShoppingCart },
  { id: 'conversion', label: 'Konwersja', icon: CheckCircle },
  { id: 'products', label: 'Popularne produkty', icon: Package },
];

const StatCard = ({ label, value, sub, color = '#10b981', icon: Icon }) => (
  <div className="glass-card p-5 flex items-center gap-4">
    {Icon && (
      <div style={{ background: color + '20', borderRadius: 12, padding: 10 }}>
        <Icon size={22} color={color} />
      </div>
    )}
    <div>
      <p className="text-[#6B7280] text-xs mb-1">{label}</p>
      <p className="text-white text-2xl font-bold">{value}</p>
      {sub && <p className="text-[#6B7280] text-xs mt-0.5">{sub}</p>}
    </div>
  </div>
);

const ReminderBadge = ({ sent, label }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: sent ? '#10b98122' : '#374151',
    color: sent ? '#10b981' : '#6B7280',
    borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, marginRight: 4,
  }}>
    {sent ? <CheckCircle size={11} /> : <Clock size={11} />}
    {label}
  </span>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#111827', border: '1px solid #1f2937', borderRadius: 8, padding: '10px 14px' }}>
      <p style={{ color: '#9CA3AF', fontSize: 12, marginBottom: 4 }}>{label}</p>
      <p style={{ color: '#10b981', fontSize: 14, fontWeight: 700 }}>{payload[0]?.value?.toFixed(2)} zł</p>
      {payload[1] && <p style={{ color: '#6B7280', fontSize: 12 }}>{payload[1]?.value} zamówień</p>}
    </div>
  );
};

const AdminAnalytics = () => {
  const [tab, setTab] = useState('sales');
  const [period, setPeriod] = useState('30d');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [selMonth, setSelMonth] = useState('');
  const [selYear, setSelYear] = useState('');
  const [stats, setStats] = useState(null);
  const [carts, setCarts] = useState([]);
  const [cartsTotal, setCartsTotal] = useState(0);
  const [cartsPage, setCartsPage] = useState(1);
  const [cartsPages, setCartsPages] = useState(1);
  const [cartsStatus, setCartsStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [cartsLoading, setCartsLoading] = useState(false);

  const token = sessionStorage.getItem('adminToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const q = (rangeStart && rangeEnd)
        ? `start=${rangeStart}&end=${rangeEnd}`
        : `period=${period}`;
      const res = await axios.get(`${API}/admin/analytics/stats?${q}`, { headers });
      setStats(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [period, rangeStart, rangeEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCarts = useCallback(async () => {
    setCartsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics/abandoned-carts?page=${cartsPage}&limit=20&status=${cartsStatus}`, { headers });
      setCarts(res.data.carts || []);
      setCartsTotal(res.data.total || 0);
      setCartsPages(res.data.pages || 1);
    } catch (e) {
      console.error(e);
    } finally {
      setCartsLoading(false);
    }
  }, [cartsPage, cartsStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { if (tab === 'carts') fetchCarts(); }, [tab, fetchCarts]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Day-only PL date (YYYY-MM-DD → dd.mm.yyyy), UTC so no off-by-one
  const fmtDay = (ds) => {
    if (!ds) return '—';
    const d = new Date(ds + 'T00:00:00Z');
    return d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
  };

  const MONTHS_PL = ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec', 'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'];
  // Year list: past + future (2025 … current+2), so future months are selectable ahead of time
  const yearOptions = React.useMemo(() => {
    const y = new Date().getFullYear();
    const arr = [];
    for (let yr = y + 2; yr >= 2025; yr--) arr.push(yr);
    return arr;
  }, []);

  // Apply a month (1-12) + year as an explicit date range. Works for future months —
  // the backend returns whatever exists, so data simply fills in as the month arrives.
  const applyYM = (m, y) => {
    setSelMonth(m); setSelYear(y);
    if (!m || !y) { setRangeStart(''); setRangeEnd(''); return; }
    const mm = String(m).padStart(2, '0');
    const last = new Date(Number(y), Number(m), 0).getDate();   // last day of month m (1-based)
    setRangeStart(`${y}-${mm}-01`);
    setRangeEnd(`${y}-${mm}-${String(last).padStart(2, '0')}`);
  };
  const pickPreset = (v) => { setPeriod(v); setRangeStart(''); setRangeEnd(''); setSelMonth(''); setSelYear(''); };
  const clearRange = () => { setRangeStart(''); setRangeEnd(''); setSelMonth(''); setSelYear(''); };
  const rangeActive = Boolean(rangeStart && rangeEnd);

  // Highest-revenue day in the current result set (mirrors the "Najlepszy dzień" card in Odwiedziny)
  const bestDay = React.useMemo(() => {
    let best = null;
    for (const d of (stats?.sales_chart || [])) {
      if (d.revenue > 0 && (!best || d.revenue > best.revenue)) best = d;
    }
    return best;
  }, [stats]);

  return (
    <AdminLayout title="Analityka">
      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${tab === t.id ? 'bg-[#10b981] text-white' : 'bg-[#0B0F0E] text-[#9CA3AF] hover:text-white border border-white/5'}`}>
            <t.icon size={15} />
            {t.label}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          {PERIODS.map(p => (
            <button key={p.value} onClick={() => pickPreset(p.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${period === p.value && !rangeActive ? 'bg-[#10b981]/20 text-[#10b981] border border-[#10b981]/30' : 'text-[#6B7280] hover:text-white'}`}>
              {p.label}
            </button>
          ))}
          <button onClick={fetchStats} className="px-3 py-1.5 text-[#6B7280] hover:text-white transition-colors">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Month picker + custom date range */}
      <div className="glass-card p-3 mb-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-[#6B7280] text-xs">Miesiąc:</span>
          <select value={selMonth} onChange={e => applyYM(e.target.value, selYear || String(new Date().getFullYear()))}
            className="bg-[#0B0F0E] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs">
            <option value="">— miesiąc —</option>
            {MONTHS_PL.map((name, i) => <option key={i} value={i + 1}>{name}</option>)}
          </select>
          <select value={selYear} onChange={e => applyYM(selMonth, e.target.value)}
            className="bg-[#0B0F0E] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs">
            <option value="">— rok —</option>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[#6B7280] text-xs">Zakres:</span>
          <input type="date" value={rangeStart} onChange={e => { setRangeStart(e.target.value); setSelMonth(''); setSelYear(''); }}
            className="bg-[#0B0F0E] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" />
          <span className="text-[#6B7280]">–</span>
          <input type="date" value={rangeEnd} onChange={e => { setRangeEnd(e.target.value); setSelMonth(''); setSelYear(''); }}
            className="bg-[#0B0F0E] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" />
        </div>
        {rangeActive ? (
          <div className="flex items-center gap-3">
            <span className="text-[#10b981] text-xs font-medium">Okres: {rangeStart} → {rangeEnd}</span>
            <button onClick={clearRange} className="text-[#6B7280] hover:text-white text-xs underline">Wyczyść</button>
          </div>
        ) : (
          <span className="text-[#6B7280] text-xs ml-auto">Aktualnie: {PERIODS.find(p => p.value === period)?.label}</span>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="spinner"></div></div>
      ) : (
        <>
          {/* SALES TAB */}
          {tab === 'sales' && stats && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Przychód" value={`${stats.conversion.revenue.toFixed(2)} zł`} icon={TrendingUp} />
                <StatCard label="Opłacone zamówienia" value={stats.conversion.paid} icon={CheckCircle} color="#10b981" />
                <StatCard label="Porzucone koszyki" value={stats.conversion.abandoned} icon={ShoppingCart} color="#f59e0b" />
                <StatCard label="Konwersja" value={`${stats.conversion.rate}%`} icon={MailWarning} color="#6366f1" />
              </div>
              {bestDay && (
                <div className="glass-card p-4 flex items-center gap-3">
                  <Star size={18} className="text-[#f59e0b]" />
                  <span className="text-[#9CA3AF] text-sm">
                    Najlepszy dzień w okresie: <strong className="text-white">{fmtDay(bestDay.date)}</strong> — <strong className="text-[#10b981]">{bestDay.revenue.toFixed(2)} zł</strong>
                  </span>
                </div>
              )}
              <div className="glass-card p-6">
                <h3 className="text-white font-semibold mb-4">Przychód dzienny</h3>
                {stats.sales_chart.length === 0 ? (
                  <p className="text-[#6B7280] text-sm py-8 text-center">Brak danych dla wybranego okresu</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={stats.sales_chart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                      <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                      <YAxis tick={{ fill: '#6B7280', fontSize: 11 }} width={55} tickFormatter={v => `${v} zł`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} name="Przychód" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          )}

          {/* ABANDONED CARTS TAB */}
          {tab === 'carts' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[#9CA3AF] text-sm">Łącznie: <strong className="text-white">{cartsTotal}</strong></span>
                <div className="flex gap-2 ml-auto">
                  {[['all', 'Wszystkie'], ['active', 'Aktywne'], ['converted', 'Skonwertowane']].map(([v, l]) => (
                    <button key={v} onClick={() => { setCartsStatus(v); setCartsPage(1); }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${cartsStatus === v ? 'bg-[#10b981]/20 text-[#10b981] border border-[#10b981]/30' : 'text-[#6B7280] hover:text-white'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {cartsLoading ? (
                <div className="flex justify-center py-12"><div className="spinner"></div></div>
              ) : carts.length === 0 ? (
                <div className="glass-card p-12 text-center">
                  <ShoppingCart size={40} className="text-[#6B7280] mx-auto mb-3" />
                  <p className="text-[#6B7280]">Brak porzuconych koszyków</p>
                </div>
              ) : (
                <div className="glass-card overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/5">
                        <th className="text-left text-xs text-[#6B7280] font-medium px-4 py-3">Email / Imię</th>
                        <th className="text-left text-xs text-[#6B7280] font-medium px-4 py-3">Produkty</th>
                        <th className="text-left text-xs text-[#6B7280] font-medium px-4 py-3">Kwota</th>
                        <th className="text-left text-xs text-[#6B7280] font-medium px-4 py-3">Data</th>
                        <th className="text-left text-xs text-[#6B7280] font-medium px-4 py-3">Przypomnienia</th>
                        <th className="text-left text-xs text-[#6B7280] font-medium px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {carts.map(cart => (
                        <tr key={cart.id} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                          <td className="px-4 py-3">
                            <p className="text-white text-sm font-medium">{cart.email}</p>
                            {cart.first_name && <p className="text-[#6B7280] text-xs">{cart.first_name}</p>}
                          </td>
                          <td className="px-4 py-3">
                            <div className="space-y-0.5">
                              {(cart.products || []).map((p, i) => (
                                <p key={i} className="text-[#9CA3AF] text-xs">{p.name}</p>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-[#10b981] font-semibold text-sm">{cart.total?.toFixed(2)} zł</span>
                            {cart.promo_code && <p className="text-[#6B7280] text-xs">Kod: {cart.promo_code}</p>}
                          </td>
                          <td className="px-4 py-3 text-[#6B7280] text-xs whitespace-nowrap">{formatDate(cart.created_at)}</td>
                          <td className="px-4 py-3">
                            <ReminderBadge sent={!!cart.reminder_1_sent_at} label="1h" />
                            <ReminderBadge sent={!!cart.reminder_2_sent_at} label="24h" />
                          </td>
                          <td className="px-4 py-3">
                            {cart.converted ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#10b981] bg-[#10b981]/10 px-2 py-1 rounded-lg">
                                <CheckCircle size={11} /> Kupił
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#f59e0b] bg-[#f59e0b]/10 px-2 py-1 rounded-lg">
                                <Clock size={11} /> Oczekuje
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pagination page={cartsPage} totalPages={cartsPages} onChange={setCartsPage} />
                </div>
              )}
            </div>
          )}

          {/* CONVERSION TAB */}
          {tab === 'conversion' && stats && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard label="Rozpoczęte checkouty" value={stats.conversion.abandoned} icon={ShoppingCart} color="#6366f1" />
                <StatCard label="Opłacone zamówienia" value={stats.conversion.paid} icon={CheckCircle} color="#10b981" />
                <StatCard label="Współczynnik konwersji" value={`${stats.conversion.rate}%`} icon={TrendingUp} color="#f59e0b" />
              </div>
              <div className="glass-card p-6">
                <h3 className="text-white font-semibold mb-6">Lejek konwersji</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Porzucone koszyki (checkout otwarty)', value: stats.conversion.abandoned, color: '#6366f1' },
                    { label: 'Opłacone zamówienia', value: stats.conversion.paid, color: '#10b981' },
                  ].map(item => {
                    const max = stats.conversion.abandoned || 1;
                    const pct = Math.round((item.value / max) * 100);
                    return (
                      <div key={item.label}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-[#9CA3AF]">{item.label}</span>
                          <span className="text-white font-semibold">{item.value} ({pct}%)</span>
                        </div>
                        <div className="h-2 bg-[#1f2937] rounded-full overflow-hidden">
                          <div style={{ width: `${pct}%`, background: item.color }} className="h-full rounded-full transition-all" />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[#6B7280] text-xs mt-6">
                  * Konwersja liczy się względem zapisanych porzuconych koszyków (gdy użytkownik wpisał email i kliknął zamów).
                </p>
              </div>
            </div>
          )}

          {/* POPULAR PRODUCTS TAB */}
          {tab === 'products' && stats && (
            <div className="space-y-4">
              {stats.popular_products.length === 0 ? (
                <div className="glass-card p-12 text-center">
                  <Package size={40} className="text-[#6B7280] mx-auto mb-3" />
                  <p className="text-[#6B7280]">Brak danych sprzedażowych dla wybranego okresu</p>
                </div>
              ) : (
                <div className="glass-card overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/5">
                    <h3 className="text-white font-semibold">Ranking produktów</h3>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/5">
                        <th className="text-left text-xs text-[#6B7280] font-medium px-4 py-3">#</th>
                        <th className="text-left text-xs text-[#6B7280] font-medium px-4 py-3">Produkt</th>
                        <th className="text-left text-xs text-[#6B7280] font-medium px-4 py-3">Sprzedanych</th>
                        <th className="text-left text-xs text-[#6B7280] font-medium px-4 py-3">Przychód</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.popular_products.map((p, i) => (
                        <tr key={i} className="border-b border-white/5 hover:bg-white/2">
                          <td className="px-4 py-3">
                            <span className={`text-sm font-bold ${i === 0 ? 'text-[#f59e0b]' : i === 1 ? 'text-[#9CA3AF]' : i === 2 ? 'text-[#92400e]' : 'text-[#6B7280]'}`}>
                              #{i + 1}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-white text-sm">{p.name}</td>
                          <td className="px-4 py-3">
                            <span className="text-[#10b981] font-semibold">{p.count}</span>
                            <span className="text-[#6B7280] text-xs ml-1">szt.</span>
                          </td>
                          <td className="px-4 py-3 text-[#10b981] font-semibold">{p.revenue.toFixed(2)} zł</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default AdminAnalytics;
